import type { Ref } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { isObservable, Subject, takeUntil } from 'rxjs';
import { detectIfTruncated } from '../../common-functions';
import DropDown from '../../fields/drop-down/drop-down.component';
import type { DropDownResult } from '../../fields/drop-down/drop-down-types';
import { DropDownSelectionType } from '../../fields/drop-down/drop-down-types';
import type { JsonData } from '../../generic-types';
import TooltipLegacy from '../../tooltip-component/tooltip.component';
import type { DataGridColumnSettings, DataGridDataCellBaseProps } from '../data-grid-settings.interface';

export interface DataGridDataMultiSelectCellProps extends DataGridDataCellBaseProps<Array<DropDownResult>> {
  textField?: string;
  valueField?: string;
  column: DataGridColumnSettings;
  width?: number;
}

function DataGridDataMultiSelectCell(
  {
    value,
    isEditing = false,
    textField = 'text',
    valueField = 'value',
    column,
    width,
  }: DataGridDataMultiSelectCellProps,
  forwardRef: Ref<HTMLDivElement>
) {
  const [data, setData] = useState<JsonData>();
  const destroySubject = new Subject<void>();
  const cellRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useImperativeHandle(forwardRef, () => cellRef.current as HTMLDivElement);

  useEffect(() => {
    if (cellRef.current) {
      setIsTruncated(detectIfTruncated(cellRef.current));
    }
  }, [width]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect to initialize data, column is stable
  useEffect(() => {
    if (column.getComponentData) {
      const dataResult = column.getComponentData({
        fieldType: 'COMBOBOX',
        fieldId: column.id,
        customProperties: column.customDataProperties,
      });

      if (isObservable(dataResult)) {
        dataResult.pipe(takeUntil(destroySubject)).subscribe((items) => setData(items));
      } else {
        setData(dataResult);
      }
    }

    return () => {
      destroySubject.next();
      destroySubject.complete();
    };
  }, []);

  const cellValue = Array.isArray(value)
    ? value
        .map((item) => item?.text)
        .filter((item) => item !== undefined)
        .join(', ')
    : '';

  return !isEditing ? (
    <TooltipLegacy
      description={cellValue}
      tooltipClassName="bg-mono-1"
      disabled={!isTruncated}
      tooltipZIndexCheck={true}
    >
      {/*  NOTE: if modifying the padding 'pl-2', please remember to update all other cells as well as 'CELL_PADDING_LEFT_PX' */}
      <div className="pl-2 truncate" data-testid="data-grid-cell-content" ref={cellRef}>
        {cellValue}
      </div>
    </TooltipLegacy>
  ) : (
    <DropDown
      textField={textField}
      valueField={valueField}
      selectionType={DropDownSelectionType.Multiple}
      value={value}
      data={data}
      useChips={false}
    />
  );
}

export default forwardRef(DataGridDataMultiSelectCell);
